import {
  ChangeEvent,
  ChangeEventHandler,
  FC,
  FocusEventHandler,
  KeyboardEvent,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Box, FormHelperText, FormLabel } from '@material-ui/core';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface JsonFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: boolean;
  helperText?: string | false;
  rows?: number;
  disabled?: boolean;
  placeholder?: string;
}

const FONT_FAMILY = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
const FONT_SIZE = 13;
const LINE_HEIGHT = 1.55;
const PADDING = 12;
const TAB_SIZE = 2;

// JSON editor: transparent textarea overlaid on a Prism-highlighted view.
// Both layers share font/padding/wrap rules so the visible glyphs align with
// the (invisible) textarea contents. Tab inserts spaces instead of leaving the
// field. Caller manages parsing — see parseJsonOrThrow / isValidJson below.
export const JsonField: FC<JsonFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  rows = 12,
  disabled,
  placeholder,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab' || e.metaKey || e.ctrlKey || e.altKey) return;
    e.preventDefault();
    const target = e.currentTarget;
    const { selectionStart, selectionEnd } = target;
    const insert = ' '.repeat(TAB_SIZE);
    const next = `${target.value.slice(0, selectionStart)}${insert}${target.value.slice(selectionEnd)}`;
    // Drive the change through formik via onChange.
    const synthetic = {
      ...e,
      target: { ...target, name, value: next },
      currentTarget: { ...target, name, value: next },
    } as unknown as ChangeEvent<HTMLTextAreaElement>;
    onChange(synthetic);
    // Restore caret after React commits.
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = selectionStart + insert.length;
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
      }
    });
  };

  // Trailing newline keeps the highlighted overlay tall enough on the last
  // line; otherwise the textarea shows a caret on a row the overlay omits.
  const display = value.endsWith('\n') ? `${value} ` : value;

  return (
    <Box sx={{ mb: 2, mt: 1 }}>
      <FormLabel
        htmlFor={name}
        sx={{
          color: 'text.primary',
          fontSize: 14,
          fontWeight: 500,
          display: 'block',
          mb: 0.5,
        }}
      >
        {label}
      </FormLabel>
      <Box
        sx={{
          position: 'relative',
          borderRadius: 1,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: error ? 'error.main' : 'neutral.300',
          backgroundColor: 'background.paper',
          boxShadow: '0px 1px 2px 0px rgba(9, 30, 66, 0.08)',
          overflow: 'hidden',
          minHeight: rows * FONT_SIZE * LINE_HEIGHT + PADDING * 2,
          transition: 'border-color 120ms, box-shadow 120ms',
          // Force the Prism layer to wrap exactly like the textarea underneath. The `vs`
          // theme renders <code> as `white-space: pre; display: inline`, so long lines ran
          // off the right edge and clipped while the (wrapping) textarea caret moved to the
          // next line — making the two layers diverge. Match them so glyphs stay aligned.
          '& pre, & code': {
            whiteSpace: 'pre-wrap !important',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            background: 'transparent !important',
          },
          '& code': {
            display: 'block',
          },
          '&:focus-within': {
            borderColor: error ? 'error.main' : 'primary.main',
            boxShadow: (theme) =>
              `${error ? theme.palette.error.main : theme.palette.primary.main}40 0 0 0 0.2rem`,
          },
        }}
      >
        <SyntaxHighlighter
          language="json"
          style={vs}
          PreTag="div"
          customStyle={{
            margin: 0,
            padding: PADDING,
            background: 'transparent',
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE,
            lineHeight: LINE_HEIGHT,
            minHeight: '100%',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            pointerEvents: 'none',
          }}
          codeTagProps={{
            style: {
              fontFamily: FONT_FAMILY,
              fontSize: FONT_SIZE,
              lineHeight: LINE_HEIGHT,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            },
          }}
        >
          {display || (placeholder ?? ' ')}
        </SyntaxHighlighter>
        <Box
          component="textarea"
          id={name}
          name={name}
          ref={textareaRef}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          placeholder={placeholder}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            padding: `${PADDING}px`,
            margin: 0,
            border: 'none',
            outline: 'none',
            resize: 'none',
            fontFamily: FONT_FAMILY,
            fontSize: `${FONT_SIZE}px`,
            lineHeight: LINE_HEIGHT,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            backgroundColor: 'transparent',
            color: 'transparent',
            // MUI's sx does not theme-map `caretColor`, so the token string 'text.primary'
            // emitted invalid CSS and the caret fell back to the transparent `color`
            // (invisible). Resolve the palette value explicitly so the caret shows.
            caretColor: (theme) => theme.palette.text.primary,
            // Selection still needs a visible color since the text glyphs are
            // transparent — fall back to the browser default highlight.
            '&::selection': {
              backgroundColor: 'rgba(33,150,243,0.25)',
            },
            '&::placeholder': {
              color: 'text.secondary',
              opacity: 0.6,
            },
          }}
        />
      </Box>
      {helperText && (
        <FormHelperText error={error} sx={{ mx: 1.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

JsonField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([false] as const)]),
  rows: PropTypes.number,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

// Helpers usable by callers to keep behaviour identical across forms.

export const stringifyJsonForEditor = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
};

export const parseJsonOrThrow = (value: string): unknown => {
  if (!value || !value.trim()) return {};
  return JSON.parse(value);
};

export const isValidJson = (value: string): boolean => {
  if (!value || !value.trim()) return true;
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};
